import { useEffect, useState, useRef } from 'react';
import { Camera, Upload, Sparkles, RefreshCw, ImageIcon, Wand2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import ProductCard from '@/components/ProductCard';
import ProductDetailDialog from '@/components/ProductDetailDialog';
import HackCard from '@/components/HackCard';
import PaywallDialog from '@/components/PaywallDialog';
import { PromoBadge } from '@/components/PromoBanner';
import { Link, useNavigate } from 'react-router-dom';

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = String(result).split(',')[1] || '';
      resolve({ base64, mime: file.type || 'image/jpeg' });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const resizeImage = (file, maxDim = 1024) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (Math.max(width, height) > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve({
              base64: String(reader.result).split(',')[1] || '',
              mime: 'image/jpeg',
              dataUrl: reader.result,
            });
          reader.readAsDataURL(blob);
        },
        'image/jpeg',
        0.85
      );
    };
    img.onerror = () => resolve(null);
    img.src = URL.createObjectURL(file);
  });

const Section = ({ label, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h4 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">{label}</h4>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <Badge
            key={i}
            className="bg-accent text-ink border border-rose-tint text-[11px] tracking-[0.04em] font-normal"
          >
            {it}
          </Badge>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
    <p className="text-sm font-medium text-ink mt-0.5 capitalize">{value || 'unclear'}</p>
  </div>
);

export default function StylingPage() {
  const { user, setCreditsLocal, credits, promo } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [recProducts, setRecProducts] = useState([]);
  const [recHacks, setRecHacks] = useState([]);
  const [openProduct, setOpenProduct] = useState(null);
  const [showLast, setShowLast] = useState(true);
  const [cameraOn, setCameraOn] = useState(false);
  const [paywall, setPaywall] = useState({ open: false, required: 2, balance: 0 });

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1024 }, height: { ideal: 1024 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
    } catch (e) {
      toast.error('Couldn\u2019t access your camera. Please check browser permissions.');
    }
  };

  const captureFromCamera = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const w = video.videoWidth || 800;
    const h = video.videoHeight || 800;
    const max = 1024;
    let outW = w, outH = h;
    if (Math.max(w, h) > max) {
      const scale = max / Math.max(w, h);
      outW = Math.round(w * scale);
      outH = Math.round(h * scale);
    }
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, outW, outH);
    canvas.toBlob(
      (blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          const b64 = String(dataUrl).split(',')[1] || '';
          setBase64(b64);
          setPreview(dataUrl);
          setAnalysis(null);
          stopCamera();
        };
        reader.readAsDataURL(blob);
      },
      'image/jpeg',
      0.9
    );
  };

  useEffect(() => {
    return () => stopCamera();
  }, []);

  useEffect(() => {
    if (!user) return;
    api
      .get('/styling/last')
      .then(({ data }) => {
        if (data?.analysis && showLast) {
          setAnalysis(data.analysis);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    const resized = await resizeImage(file, 1024);
    if (!resized || !resized.base64) {
      // fallback to direct read
      const { base64: b64, mime } = await readFileAsBase64(file);
      setBase64(b64);
      setPreview(`data:${mime};base64,${b64}`);
      return;
    }
    setBase64(resized.base64);
    setPreview(resized.dataUrl);
    setAnalysis(null);
  };

  const onPick = (e) => handleFile(e.target.files?.[0]);

  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const analyze = async () => {
    if (!user) {
      toast('Sign in to use AI styling', {
        action: { label: 'Sign in', onClick: () => navigate('/login') },
      });
      return;
    }
    if (!base64) {
      toast.error('Pick a clear selfie first');
      return;
    }
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const { data } = await api.post('/styling/analyze', {
        image_base64: base64,
        image_mime: 'image/jpeg',
      });
      if (typeof data?.credits === 'number') setCreditsLocal(data.credits);
      if (!data.analysis) {
        toast.error('Couldn’t read the photo. Try a clearer selfie.');
      } else {
        toast.success('Your stylist report is ready');
        setAnalysis(data.analysis);
        setRecProducts(data.recommended_products || []);
        setRecHacks(data.recommended_hacks || []);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && detail?.error === 'insufficient_credits') {
        setPaywall({
          open: true,
          required: detail.required ?? 2,
          balance: detail.balance ?? 0,
        });
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'AI analysis failed. Please try again.');
      }
    } finally {
      setAnalyzing(false);
      setShowLast(false);
    }
  };

  return (
    <div data-testid="styling-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold flex items-center gap-2">
          <Wand2 className="h-3.5 w-3.5" /> AI Beauty Styling
        </span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          Upload a selfie. Meet your virtual stylist.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          We’ll analyse your face shape, undertone, eye, hair and lip features, then suggest the makeup looks,
          hair styles, jewelry pieces and product categories most likely to suit you.
        </p>
        {!user && (
          <p className="mt-3 text-sm text-muted-foreground">
            <Link to="/login" className="underline underline-offset-4 text-foreground">Sign in</Link>{' '}
            to use AI styling and save your report.
          </p>
        )}
        {user && (
          <div className="mt-3 flex items-center gap-2 text-xs flex-wrap">
            {promo?.active ? (
              <PromoBadge />
            ) : (
              <>
                <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em]">
                  2 credits per analysis
                </Badge>
                <span className="text-muted-foreground tabular-nums">
                  · Balance: <span className="font-semibold text-foreground">{credits}</span>
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        {/* Upload + analyze panel */}
        <Card className="lg:col-span-5 bg-card border-border/70 rounded-2xl p-6">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="relative aspect-[4/5] w-full rounded-xl border border-dashed border-rose-tint bg-cream-paper overflow-hidden flex items-center justify-center"
          >
            {cameraOn ? (
              <>
                <video ref={videoRef} className="absolute inset-0 h-full w-full object-cover" playsInline muted />
                <button
                  onClick={stopCamera}
                  className="absolute top-2 right-2 bg-card/90 rounded-full p-1.5 hover:bg-card border border-border"
                  aria-label="Close camera"
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              </>
            ) : preview ? (
              <img src={preview} alt="Selfie preview" className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <div className="text-center px-6">
                <ImageIcon className="h-10 w-10 mx-auto text-rose-gold" />
                <p className="mt-3 font-display text-xl text-ink">Drop, upload or take a selfie</p>
                <p className="mt-1 text-xs text-muted-foreground">Best with bright, even lighting and a clear front-facing photo.</p>
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={onPick}
            data-testid="styling-file-input"
          />
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => fileRef.current?.click()}
              data-testid="styling-pick-file-button"
            >
              <Upload className="h-4 w-4 mr-1" /> Upload
            </Button>
            {cameraOn ? (
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={captureFromCamera}
                data-testid="styling-capture-button"
              >
                <Camera className="h-4 w-4 mr-1" /> Capture
              </Button>
            ) : (
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={startCamera}
                data-testid="styling-start-camera-button"
              >
                <Camera className="h-4 w-4 mr-1" /> Camera
              </Button>
            )}
            <Button
              className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              onClick={analyze}
              disabled={analyzing || !base64}
              data-testid="styling-analyze-button"
            >
              {analyzing ? (
                <><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Analyzing</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-1" /> Analyze</>
              )}
            </Button>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
            <Camera className="h-3 w-3 inline -mt-0.5 mr-1" />
            Your photo is sent securely to Gemini 2.5 for analysis. We don’t store the image—only the styling notes you see here.
          </p>
        </Card>

        {/* Analysis panel */}
        <Card className="lg:col-span-7 bg-cream-paper border-border/70 rounded-2xl p-6 sm:p-8 min-h-[420px]">
          {analyzing ? (
            <div className="space-y-4">
              <Skeleton className="h-7 w-2/3" />
              <Skeleton className="h-20 w-full" />
              <div className="grid grid-cols-2 gap-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
              <Skeleton className="h-16 w-full" />
            </div>
          ) : analysis ? (
            <div className="space-y-5" data-testid="styling-analysis">
              <div>
                <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold">Stylist notes</span>
                <h3 className="mt-1 font-display text-2xl text-ink leading-tight">
                  {analysis.stylist_summary || 'Your personalized styling report'}
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Field label="Face shape" value={analysis.face_shape} />
                <Field label="Undertone" value={analysis.skin_undertone} />
                <Field label="Eye color" value={analysis.eye_color} />
                <Field label="Lips" value={analysis.lip_shape} />
              </div>
              {analysis.hair && (
                <div className="grid grid-cols-3 gap-4">
                  <Field label="Hair color" value={analysis.hair?.color} />
                  <Field label="Hair type" value={analysis.hair?.type} />
                  <Field label="Hair length" value={analysis.hair?.length} />
                </div>
              )}
              <Section label="Best makeup looks" items={analysis.best_makeup_looks} />
              <Section label="Lip shades to try" items={analysis.recommended_lip_shades} />
              <Section label="Eye looks" items={analysis.recommended_eye_looks} />
              <Section label="Hair styles" items={analysis.recommended_hair_styles} />
              <Section label="Jewelry direction" items={analysis.recommended_jewelry} />
              {analysis.recommended_perfume_family && (
                <Section label="Perfume family" items={[analysis.recommended_perfume_family]} />
              )}
              <Section label="Skincare priorities" items={analysis.skincare_priorities} />
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Sparkles className="h-10 w-10 text-rose-gold" />
              <p className="mt-3 font-display text-2xl text-ink">Your stylist report appears here</p>
              <p className="mt-1 text-sm text-muted-foreground max-w-sm">
                Upload a clear selfie on the left, then click “Get my report”.
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Recommendations from AI */}
      {(recProducts.length > 0 || recHacks.length > 0) && (
        <section className="mt-12" data-testid="styling-recommendations">
          <span className="text-[11px] uppercase tracking-[0.18em] text-rose-gold">Curated for your stylist report</span>
          <h2 className="mt-2 font-display text-3xl font-semibold text-ink mb-6">Try these next</h2>
          {recProducts.length > 0 && (
            <>
              <h3 className="text-sm uppercase tracking-[0.14em] text-muted-foreground mb-3">Products</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
                {recProducts.map((p) => (
                  <ProductCard key={p.id} product={p} onOpen={setOpenProduct} />
                ))}
              </div>
            </>
          )}
          {recHacks.length > 0 && (
            <>
              <h3 className="text-sm uppercase tracking-[0.14em] text-muted-foreground mb-3">DIY rituals</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {recHacks.map((h) => (
                  <HackCard key={h.id} hack={h} />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      <ProductDetailDialog
        product={openProduct}
        open={!!openProduct}
        onOpenChange={(o) => !o && setOpenProduct(null)}
      />

      <PaywallDialog
        open={paywall.open}
        onOpenChange={(o) => setPaywall((p) => ({ ...p, open: o }))}
        feature="styling_analyze"
        required={paywall.required}
        balance={paywall.balance}
      />
    </div>
  );
}
